import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

type PageHeaderProps = {
  /** Small uppercase line above the title: a date, a section, a count. */
  eyebrow?: ReactNode;
  title: ReactNode;
  /** One line under the title saying what the page is for. */
  lede?: ReactNode;
  /** The page's actions; at most one of them is the petrol primary. */
  actions?: ReactNode;
  className?: string;
};

/**
 * The page header of the design system (docs/architecture/design-system.md §5):
 * eyebrow → display title → lede → primary action on the right.
 */
export function PageHeader({ eyebrow, title, lede, actions, className }: PageHeaderProps) {
  return (
    <header
      className={cn(
        "flex flex-wrap items-end justify-between gap-x-6 gap-y-4",
        className
      )}
    >
      <div className="min-w-0 flex-[1_1_20rem]">
        {eyebrow ? (
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-app-fg-muted">
            {eyebrow}
          </p>
        ) : null}
        <h1 className="mt-1 font-display text-[2.125rem] font-medium leading-[1.05] tracking-[-0.02em] text-app-fg-strong [text-wrap:balance]">
          {title}
        </h1>
        {lede ? (
          <p className="mt-2 max-w-prose text-sm text-app-fg-muted sm:text-base">{lede}</p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex max-w-full flex-wrap items-center gap-2 sm:justify-end">{actions}</div>
      ) : null}
    </header>
  );
}

/**
 * The section of an eyebrow, as a link. Pages with no menu entry of their own
 * reached their section through a breadcrumb; the eyebrow replaced the
 * breadcrumb, so it keeps the way back instead of only naming it.
 */
export function PageEyebrowLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="rounded-sm underline-offset-4 hover:text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      {children}
    </Link>
  );
}
